import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import CustomModal from '../../../util_components/custom_modal';
import CustomLoading from '../../../util_components/custom_loading';
import { get_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';

const formatDate = (d) => (d ? moment(d).format('MMM Do, YYYY') : '—');

const rowLabel = (row) => {
  if (row.trial_type === 'initial') return 'Initial Trial';
  return `Trial Restart ${row.restart_index}`;
};

const withRestartIndex = (rows) => {
  let restartCount = 0;
  return rows.map((row) => {
    if (row.trial_type === 'restart') {
      restartCount += 1;
      return { ...row, restart_index: restartCount };
    }
    return { ...row, restart_index: 0 };
  });
};

function TrialHistoryTable({ data }) {
  const rows = withRestartIndex(data);
  return (
    <table className="min-w-full border-collapse">
      <thead>
        <tr className="border-b">
          <th aria-label="Trial label" className="text-center px-3 py-2 text-sm font-semibold" />
          <th className="text-center px-3 py-2 text-sm font-semibold">Trial Start</th>
          <th className="text-center px-3 py-2 text-sm font-semibold">Trial End</th>
          <th className="text-center px-3 py-2 text-sm font-semibold">Days</th>
          <th className="text-center px-3 py-2 text-sm font-semibold">Reset by</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr key={`${row.trial_type}-${row.trial_start_date}-${idx}`} className="border-b">
            <td className="px-3 py-2 text-sm font-medium">{rowLabel(row)}</td>
            <td className="px-3 py-2 text-sm">{formatDate(row.trial_start_date)}</td>
            <td className="px-3 py-2 text-sm">{formatDate(row.trial_end_date)}</td>
            <td className="px-3 py-2 text-sm">{row.days ?? '—'}</td>
            <td className="px-3 py-2 text-sm">{row.reset_by || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

TrialHistoryTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      trial_type: PropTypes.oneOf(['initial', 'restart']),
      trial_start_date: PropTypes.string,
      trial_end_date: PropTypes.string,
      days: PropTypes.number,
      reset_by: PropTypes.string
    })
  ).isRequired
};

function TrialHistoryModal({ show, close, student_uuid }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState('none');

  const fetchHistory = useCallback(() => {
    if (!student_uuid) return;
    setLoading('loading');
    const url = get_api_url(`/v2/cms_user/api/get_trial_history?student_uuid=${student_uuid}`);
    get_api(url, true)
      .then((response) => {
        const payload = response?.data || {};
        setData(payload.trial_history || []);
        setLoading('success');
      })
      .catch(() => {
        setLoading('fail');
      });
  }, [student_uuid]);

  useEffect(() => {
    if (show) fetchHistory();
  }, [show, fetchHistory]);

  const renderBody = () => {
    if (loading === 'loading') {
      return (
        <div className="flex justify-center py-8">
          <CustomLoading />
        </div>
      );
    }
    if (loading === 'fail') {
      return (
        <div className="py-6 text-center">
          <p className="mb-3">Could not load trial history</p>
          <button
            type="button"
            onClick={fetchHistory}
            className="px-4 py-2 bg-mytprimary text-white rounded"
          >
            Retry
          </button>
        </div>
      );
    }
    if (!data || data.length === 0) {
      return <div className="py-6 text-center text-gray-500">No trial history available.</div>;
    }
    return (
      <div className="overflow-x-auto">
        <TrialHistoryTable data={data} />
      </div>
    );
  };

  return (
    <CustomModal show={show} close={close} title="Trial History">
      {renderBody()}
    </CustomModal>
  );
}

TrialHistoryModal.propTypes = {
  show: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  student_uuid: PropTypes.string
};

TrialHistoryModal.defaultProps = {
  student_uuid: ''
};

export default TrialHistoryModal;
